import { useEffect, useRef, useState } from "react";
import { useAuth } from "@clerk/react";
import type { Gallery, Photo } from "./model";

export async function readJson<T>(response: Response): Promise<T> {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(typeof data.error === "string" ? data.error : "Request failed");
  return data as T;
}

export async function authedFetch(
  getToken: () => Promise<string | null>,
  input: RequestInfo | URL,
  init: RequestInit = {}
) {
  const token = await getToken();
  const headers = new Headers(init.headers);
  if (token) headers.set("authorization", `Bearer ${token}`);
  return fetch(input, { ...init, headers });
}

export type GalleryData = { gallery: Gallery; photos: Photo[]; userId: string | null };

export async function fetchGalleryData(slug: string, token: string | null, signal?: AbortSignal) {
  const headers = token ? { authorization: `Bearer ${token}` } : undefined;
  const [galleryResponse, photosResponse] = await Promise.all([
    fetch(`/api/galleries/${slug}`, { headers, signal }),
    fetch(`/api/galleries/${slug}/photos`, { headers, signal })
  ]);
  const galleryData = await readJson<{ gallery: Gallery }>(galleryResponse);
  const photoData = await readJson<{ photos: Photo[] }>(photosResponse);
  return { gallery: galleryData.gallery, photos: photoData.photos };
}

export function useGalleryData(slug: string, initialData?: GalleryData) {
  const { getToken, isLoaded, isSignedIn, userId } = useAuth();
  const [gallery, setGallery] = useState<Gallery | null>(initialData?.gallery ?? null);
  const [photos, setPhotos] = useState<Photo[]>(initialData?.photos ?? []);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(!initialData);
  const [version, setVersion] = useState(0);
  const seedIsCurrent = useRef(true);

  useEffect(() => {
    if (!isLoaded) return;
    // Pointer navigation already fetched this gallery for the opening transition.
    // Reloads and account changes still fetch fresh data.
    if (seedIsCurrent.current && initialData?.gallery.slug === slug && initialData.userId === (userId ?? null) && version === 0) return;
    seedIsCurrent.current = false;
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError("");
      try {
        const token = isSignedIn ? await getToken() : null;
        const data = await fetchGalleryData(slug, token);
        if (!cancelled) {
          setGallery(data.gallery);
          setPhotos(data.photos);
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : "Could not load gallery");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [getToken, isLoaded, isSignedIn, userId, slug, version, initialData]);

  return { gallery, photos, setPhotos, error, loading, reload: () => setVersion((value) => value + 1) };
}

export function useUserGalleries() {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const [galleries, setGalleries] = useState<Gallery[]>([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    if (!isLoaded) return;
    if (!isSignedIn) {
      setGalleries([]);
      setError("");
      setLoading(false);
      return;
    }

    let cancelled = false;
    async function load() {
      setLoading(true);
      setError("");
      try {
        const response = await authedFetch(getToken, "/api/galleries");
        const data = await readJson<{ galleries: Gallery[] }>(response);
        if (!cancelled) setGalleries(Array.isArray(data.galleries) ? data.galleries : []);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : "Could not load albums");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [getToken, isLoaded, isSignedIn, version]);

  return { galleries, error, loading, reload: () => setVersion((value) => value + 1) };
}

export function useSyncSignedInUser() {
  const { isLoaded, isSignedIn, getToken } = useAuth();

  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;
    let cancelled = false;

    async function syncUser() {
      try {
        if (!cancelled) await authedFetch(getToken, "/api/me", { method: "POST" });
      } catch {
        // Auth sync is retried on the next signed-in page load.
      }
    }

    void syncUser();
    return () => {
      cancelled = true;
    };
  }, [getToken, isLoaded, isSignedIn]);
}
